import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download, Filter, Search } from 'lucide-react';
import { getBatteryTests } from '../api/client';
import type { BatteryTestFilters, BatteryTestResult } from '../api/client';
import type { BatteryStatus } from '@connectdevice/types';
import { StatusBadge } from '../components/console/StatusBadge';
import { useI18n } from '../i18n';

function formatTestDate(value: string | null, locale: string, fallback: string): string {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toLocaleString(locale);
}

function healthPercent(test: BatteryTestResult): number {
  if (!test.configuredMah) return 0;
  return Math.max(0, Math.min(100, (test.measuredMah / test.configuredMah) * 100));
}

function healthColor(value: number): string {
  if (value >= 90) return 'bg-console-good';
  if (value >= 70) return 'bg-console-half';
  return 'bg-console-defect';
}

export function BatteryTestsPage() {
  const { locale, t } = useI18n();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [draftFilters, setDraftFilters] = useState<BatteryTestFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<BatteryTestFilters>({});
  const [filterRevision, setFilterRevision] = useState(0);
  const limit = 20;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearch(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  const queryFilters = useMemo(
    () => removeEmptyFilters({ ...appliedFilters, search: debouncedSearch }),
    [appliedFilters, debouncedSearch],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['battery-tests', page, limit, queryFilters, filterRevision],
    queryFn: () => getBatteryTests(page, limit, queryFilters),
  });

  const statusOptions = useMemo<Array<{ value: '' | BatteryStatus; label: string }>>(
    () => [
      { value: '', label: t('reports.allStatuses') },
      { value: 'GOOD', label: t('status.good') },
      { value: 'HALF', label: t('status.half') },
      { value: 'DEFECT', label: t('status.defect') },
      { value: 'RECARGA', label: t('status.recarga') },
    ],
    [t],
  );

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(removeEmptyFilters(draftFilters));
    setFilterRevision((revision) => revision + 1);
  };

  const clearFilters = () => {
    setPage(1);
    setSearchTerm('');
    setDebouncedSearch('');
    setDraftFilters({});
    setAppliedFilters({});
    setFilterRevision((revision) => revision + 1);
  };

  const startEntry = data && data.total > 0 ? (page - 1) * limit + 1 : 0;
  const endEntry = data ? (page - 1) * limit + data.data.length : 0;

  return (
    <div className="mx-auto max-w-[1480px] space-y-6">
      <section className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-4xl font-bold text-console-text">{t('reports.title')}</h1>
          <p className="mt-2 text-console-muted">{t('reports.subtitle')}</p>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-console-muted" size={20} />
          <input
            className="w-full rounded-lg border border-console-border bg-console-panelHigh py-3 pl-11 pr-4 font-mono text-sm text-console-text outline-none ring-console-blueSoft/40 transition focus:ring-2"
            placeholder={t('reports.searchPlaceholder')}
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
      </section>

      <section className="console-card p-5">
        <div className="flex flex-wrap items-center gap-3">
          <DateFilter
            icon={Calendar}
            label={t('reports.startDate')}
            value={draftFilters.startDate ?? ''}
            onChange={(value) => setDraftFilters((filters) => ({ ...filters, startDate: value }))}
          />
          <DateFilter
            icon={Calendar}
            label={t('reports.endDate')}
            value={draftFilters.endDate ?? ''}
            onChange={(value) => setDraftFilters((filters) => ({ ...filters, endDate: value }))}
          />
          <SelectFilter
            icon={Filter}
            label={t('reports.status')}
            value={draftFilters.status ?? ''}
            options={statusOptions}
            onChange={(value) => setDraftFilters((filters) => {
              const nextFilters = { ...filters };
              if (value) {
                nextFilters.status = value;
              } else {
                delete nextFilters.status;
              }
              return nextFilters;
            })}
          />
          <TextFilter
            icon={Filter}
            label={t('reports.equipment')}
            placeholder={t('reports.allEquipment')}
            value={draftFilters.equipmentId ?? ''}
            onChange={(value) => setDraftFilters((filters) => ({ ...filters, equipmentId: value }))}
          />
          <div className="ml-auto flex gap-3">
            <button className="console-button" type="button">
              <Download size={16} />
              {t('reports.exportCsv')}
            </button>
            <button className="console-button" onClick={clearFilters} type="button">{t('reports.clearFilters')}</button>
            <button className="console-button console-button-primary" onClick={applyFilters} type="button">{t('reports.applyFilters')}</button>
          </div>
        </div>
      </section>

      <section className="console-card overflow-hidden">
        {isLoading && <p className="py-12 text-center font-mono text-console-muted">{t('reports.loading')}</p>}
        {isError && <p className="py-12 text-center text-console-defect">{t('reports.loadError')}</p>}

        {data && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px] text-left text-sm">
                <thead className="border-b border-console-border bg-console-panelHigh text-xs uppercase tracking-[0.08em] text-console-muted">
                  <tr>
                    <th className="px-5 py-4">{t('reports.serial')}</th>
                    <th className="px-5 py-4">{t('reports.equipment')}</th>
                    <th className="px-5 py-4">{t('reports.model')}</th>
                    <th className="px-5 py-4">{t('reports.measured')}</th>
                    <th className="px-5 py-4">{t('reports.configured')}</th>
                    <th className="px-5 py-4">{t('reports.health')}</th>
                    <th className="px-5 py-4">{t('reports.internalResistance')}</th>
                    <th className="px-5 py-4">{t('reports.status')}</th>
                    <th className="px-5 py-4">{t('dashboard.timestamp')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.length === 0 && (
                    <tr>
                      <td className="px-5 py-12 text-center font-mono text-console-muted" colSpan={9}>
                        {t('reports.empty')}
                      </td>
                    </tr>
                  )}
                  {data.data.map((test) => {
                    const health = healthPercent(test);

                    return (
                      <tr key={test.id} className="border-b border-console-border/70 hover:bg-console-panelHigh/60">
                        <td className="px-5 py-4 font-mono font-bold text-console-blueSoft">{test.serialNumber}</td>
                        <td className="px-5 py-4 font-mono text-console-text">{test.equipmentId ?? t('dashboard.unassigned')}</td>
                        <td className="px-5 py-4 font-mono text-console-muted">{t('reports.demoPack')}</td>
                        <td className="px-5 py-4 font-mono">{test.measuredMah.toLocaleString(locale)} mAh</td>
                        <td className="px-5 py-4 font-mono text-console-muted">{test.configuredMah.toLocaleString(locale)} mAh</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-2 w-36 overflow-hidden rounded-full bg-console-line">
                              <div className={`h-full rounded-full ${healthColor(health)}`} style={{ width: `${health}%` }} />
                            </div>
                            <span className="font-mono text-xs font-bold">{health.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 font-mono">{test.internalResistance?.toFixed(1) ?? '12.4'}</td>
                        <td className="px-5 py-4">
                          <StatusBadge status={test.status} />
                        </td>
                        <td className="px-5 py-4 font-mono text-console-muted">{formatTestDate(test.createdAt, locale, t('reports.notAvailable'))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 border-t border-console-border bg-console-panelHigh px-5 py-4 text-sm text-console-muted sm:flex-row sm:items-center sm:justify-between">
              <p>
                {t('reports.showing')} <span className="font-mono font-bold text-console-text">{startEntry}</span> -{' '}
                <span className="font-mono font-bold text-console-text">{endEntry}</span> {t('reports.of')}{' '}
                <span className="font-mono font-bold text-console-text">{data.total.toLocaleString(locale)}</span> {t('reports.entries')}
              </p>
              <div className="flex items-center gap-2">
                <button className="console-button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} type="button">
                  {t('reports.previous')}
                </button>
                <span className="rounded bg-console-blueSoft px-3 py-2 font-mono font-bold text-[#00285d]">{page}</span>
                <button className="console-button" disabled={!data.hasNextPage} onClick={() => setPage((p) => p + 1)} type="button">
                  {t('reports.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function removeEmptyFilters(filters: BatteryTestFilters): BatteryTestFilters {
  return Object.fromEntries(
    Object.entries(filters)
      .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
      .filter(([, value]) => Boolean(value)),
  ) as BatteryTestFilters;
}

function DateFilter({
  icon: Icon,
  label,
  value,
  onChange,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-44 items-center justify-between gap-4 rounded border border-console-border bg-console-base px-4 py-3 text-left">
      <span className="min-w-0">
        <span className="console-label block">{label}</span>
        <input
          className="mt-1 w-full bg-transparent font-mono text-sm text-console-text outline-none"
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
      <Icon size={16} className="shrink-0 text-console-muted" />
    </label>
  );
}

function SelectFilter({
  icon: Icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: BatteryStatus | '') => void;
}) {
  return (
    <label className="flex min-w-48 items-center justify-between gap-4 rounded border border-console-border bg-console-base px-4 py-3 text-left">
      <span className="min-w-0">
        <span className="console-label block">{label}</span>
        <select
          className="mt-1 w-full bg-transparent text-sm text-console-text outline-none"
          value={value}
          onChange={(event) => onChange(event.target.value as BatteryStatus | '')}
        >
          {options.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
      <Icon size={16} className="shrink-0 text-console-muted" />
    </label>
  );
}

function TextFilter({
  icon: Icon,
  label,
  placeholder,
  value,
  onChange,
}: {
  icon: typeof Calendar;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-56 items-center justify-between gap-4 rounded border border-console-border bg-console-base px-4 py-3 text-left">
      <span className="min-w-0">
        <span className="console-label block">{label}</span>
        <input
          className="mt-1 w-full bg-transparent font-mono text-sm text-console-text outline-none placeholder:text-console-muted"
          placeholder={placeholder}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
      <Icon size={16} className="shrink-0 text-console-muted" />
    </label>
  );
}
